import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { setLoading } from '../../../store/actions';
import { getPropertyList } from '../../Inventory/pages/FacilityInventory/Helpers/Api';
import {
  getVIRReports,
  getVIRCategories,
  getVIRStatusCounts,   // 👈 new
  createVIRReport,
  updateVIRReport,
} from './Components/FormikHelpers/Api';
import { VIRReportsTable, VIRCategoriesTable } from './Components/VendorInspectionReportTables';
import { VIRVendorsTable } from './Components/VendorsTable';
import { VendorsByCategoryModal } from './Components/VendorsByCategoryModal';
import './Components/vir.scss';

const TABS = {
  REPORTS: 'reports',
  CATEGORIES: 'categories',
  VENDORS: 'vendors',
};

// Status radio config — mirrors Incident Report's statusButtons
const VIR_STATUS_BUTTONS = [
  { value: 'all',                  label: 'All',                  icon: 'list',          color: 'secondary' },
  { value: 'Open',                 label: 'Open',                 icon: 'alert-triangle', color: 'warning'  },
  { value: 'In Progress',          label: 'In Progress',          icon: 'activity',       color: 'success'  },
  { value: 'Completed',            label: 'Completed',            icon: 'check-circle',   color: 'info'     },
  { value: 'Not Able to Complete', label: 'Not Able to Complete', icon: 'x-circle',       color: 'danger'   },
];

const MONTH_OPTIONS = [
  { value: '', label: 'All Months' },
  ...['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => ({
    value: String(i + 1),
    label: m,
  })),
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [
  { value: '', label: 'All Years' },
  ...Array.from({ length: 6 }, (_, i) => {
    const y = currentYear - i;
    return { value: String(y), label: String(y) };
  }),
];

const selectStyles = {
  control: (base, { isFocused, isDisabled }) => ({
    ...base,
    borderColor: isFocused ? '#4F46E5' : '#E2E8F0',
    borderRadius: 8,
    minHeight: 34,
    fontSize: '0.80rem',
    boxShadow: isFocused ? '0 0 0 3px rgba(79,70,229,0.12)' : 'none',
    minWidth: 140,
    backgroundColor: isDisabled ? '#f8f9fa' : base.backgroundColor,
    cursor: isDisabled ? 'not-allowed' : 'default',
    '&:hover': { borderColor: '#4F46E5' },
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    fontSize: '0.80rem',
    background: isSelected ? '#4F46E5' : isFocused ? '#EEF2FF' : 'white',
    color: isSelected ? 'white' : '#0F172A',
  }),
  menu: (base) => ({ ...base, borderRadius: 8, border: '1px solid #E2E8F0', zIndex: 999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const emptyFilters = {
  propertyId: '',
  buildingName: '',
  year: '',
  month: '',
  status: 'all',   // 👈 changed default from '' to 'all'
  categoryId: '',
};

// Color map for the status radio labels
const STATUS_COLOR_MAP = {
  secondary: '#64748B',
  warning:   '#D97706',
  success:   '#059669',
  info:      '#0284C7',
  danger:    '#DC2626',
};

const VendorInspectionReport = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(TABS.REPORTS);
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [reports, setReports] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [filters, setFilters] = useState({ ...emptyFilters });
  const [vendorsByCatOpen, setVendorsByCatOpen] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});  // 👈 new

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch { return null; }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;
    setUser(currentUser);
    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, getUserFromToken]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getVIRCategories({ activeOnly: 'true' });
      setCategoryOptions((res?.data || []).map((c) => ({ value: c._id, label: c.name })));
    } catch (err) {
      console.error('Failed to fetch VIR categories:', err);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    setTableLoading(true);
    try {
      const params = {};
      if (filters.propertyId)  params.propertyId  = filters.propertyId;
      if (filters.buildingName) params.buildingName = filters.buildingName;
      if (filters.year)        params.year        = filters.year;
      if (filters.month)       params.month       = filters.month;
      // send status only when not 'all'
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.categoryId)  params.categoryId  = filters.categoryId;

      const res = await getVIRReports(params);
      setReports(res?.data || []);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setReports([]);
    } finally {
      setTableLoading(false);
    }
  }, [filters]);

  // 👇 new — mirrors fetchStatusCounts in IncidentReport
  const fetchStatusCounts = useCallback(async () => {
    try {
      const params = {};
      if (filters.propertyId)   params.propertyId   = filters.propertyId;
      if (filters.buildingName)  params.buildingName  = filters.buildingName;
      if (filters.year)         params.year         = filters.year;
      if (filters.month)        params.month        = filters.month;
      if (filters.categoryId)   params.categoryId   = filters.categoryId;

      const res = await getVIRStatusCounts(params);
      setStatusCounts(res?.data || {});
    } catch (err) {
      console.error('Failed to fetch VIR status counts:', err);
      setStatusCounts({});
    }
  }, [filters]);

  const handleSaveReport = useCallback(async (formData, isNew, existingId) => {
    if (isNew) return await createVIRReport(formData);
    return await updateVIRReport(existingId, formData);
  }, []);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => {
    if (activeTab === TABS.REPORTS) {
      fetchReports();
      fetchStatusCounts();  // 👈 also refresh counts when filters change
    }
  }, [fetchReports, fetchStatusCounts, activeTab]);

  // Auto-select building when property has exactly one building
  useEffect(() => {
    if (!filters.propertyId) return;
    const selectedProperty = properties.find((p) => p._id === filters.propertyId);
    if (!selectedProperty) return;
    const validBuildings = (selectedProperty.building || []).filter((b) => b?.buildingName);
    if (validBuildings.length === 1) {
      setFilters((prev) => ({ ...prev, buildingName: validBuildings[0].buildingName }));
    } else {
      const belongs = validBuildings.some((b) => b.buildingName === filters.buildingName);
      if (!belongs) setFilters((prev) => ({ ...prev, buildingName: '' }));
    }
  }, [filters.propertyId, properties]);

  // Active filter count excludes status:'all' and the status key itself
  const activeFilterCount = [
    filters.propertyId, filters.categoryId, filters.month, filters.year,
    filters.status !== 'all' ? filters.status : '',
  ].filter(Boolean).length;

  const propertyOptions = [
    { value: '', label: 'All Properties' },
    ...(properties || []).map((p) => ({ value: p._id, label: p.title || p.name || p._id })),
  ];

  const buildingOptions = (() => {
    if (!filters.propertyId) return [{ value: '', label: 'All Buildings' }];
    const prop = properties.find((p) => p._id === filters.propertyId);
    const buildings = (prop?.building || []).filter((b) => b?.buildingName);
    return [
      { value: '', label: 'All Buildings' },
      ...buildings.map((b) => ({ value: b.buildingName, label: b.buildingName })),
    ];
  })();

  const hasSingleBuilding = (() => {
    if (!filters.propertyId) return false;
    const prop = properties.find((p) => p._id === filters.propertyId);
    return ((prop?.building || []).filter((b) => b?.buildingName)).length === 1;
  })();

  const allCategoryOptions = [{ value: '', label: 'All Categories' }, ...categoryOptions];

  const tabStyle = (tab) => ({
    cursor: 'pointer',
    padding: '9px 22px',
    fontWeight: 700,
    fontSize: '0.85rem',
    color: activeTab === tab ? '#4F46E5' : '#64748B',
    borderBottom: activeTab === tab ? '2px solid #4F46E5' : '2px solid transparent',
    background: 'none',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.15s ease',
  });

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Vendor Inspection Report</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Maintenance" breadcrumbItem="Vendor Inspection Report" />

        {/* ── Tabs ── */}
        <div style={{
          display: 'flex', alignItems: 'center', marginBottom: 24,
          borderBottom: '1px solid #E2E8F0', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button style={tabStyle(TABS.REPORTS)} onClick={() => setActiveTab(TABS.REPORTS)}>
              <FeatherIcon icon="file-text" size={14} /> Reports
            </button>
            <button style={tabStyle(TABS.CATEGORIES)} onClick={() => setActiveTab(TABS.CATEGORIES)}>
              <FeatherIcon icon="layers" size={14} /> Categories
            </button>
            <button style={tabStyle(TABS.VENDORS)} onClick={() => setActiveTab(TABS.VENDORS)}>
              <FeatherIcon icon="briefcase" size={14} /> Vendors
            </button>
          </div>

          <button
            type="button"
            onClick={() => setVendorsByCatOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'linear-gradient(135deg,#4F46E5,#7C3AED)',
              color: 'white', border: 'none', borderRadius: 8,
              padding: '7px 16px', fontSize: '0.80rem', fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 2px 8px rgba(79,70,229,0.3)', marginBottom: 1,
            }}
          >
            <FeatherIcon icon="briefcase" size={13} /> Vendors by Category
          </button>
        </div>

        {activeTab === TABS.REPORTS && (
          <>
            {/* ── Status radio row (new) ── */}
            <Form className="mb-3">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                {VIR_STATUS_BUTTONS.map((s) => (
                  <FormGroup check inline key={s.value} style={{ marginBottom: 0 }}>
                    <Input
                      type="radio"
                      name="virStatus"
                      checked={filters.status === s.value}
                      onChange={() => setFilters((f) => ({ ...f, status: s.value }))}
                    />
                    <Label check style={{ color: STATUS_COLOR_MAP[s.color], fontWeight: 600, fontSize: '0.83rem', cursor: 'pointer' }}>
                      <FeatherIcon icon={s.icon} size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {s.label}
                      {statusCounts[s.value] !== undefined && (
                        <span style={{
                          marginLeft: 5,
                          background: '#F1F5F9',
                          color: '#475569',
                          borderRadius: 20,
                          padding: '1px 7px',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                        }}>
                          {statusCounts[s.value]}
                        </span>
                      )}
                    </Label>
                  </FormGroup>
                ))}
              </div>
            </Form>

            {/* ── Existing filter bar ── */}
            <div style={{
              background: 'white', border: '1px solid #E2E8F0', borderRadius: 12,
              padding: '14px 18px', marginBottom: 16, display: 'flex',
              flexWrap: 'wrap', gap: 12, alignItems: 'center',
              boxShadow: '0 1px 4px rgba(15,23,42,0.05)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 4 }}>
                <FeatherIcon icon="filter" size={14} color="#4F46E5" />
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Filters
                </span>
              </div>

              <div style={{ minWidth: 180 }}>
                <Select
                  options={propertyOptions}
                  value={propertyOptions.find((o) => o.value === filters.propertyId) || propertyOptions[0]}
                  onChange={(opt) => setFilters((f) => ({ ...f, propertyId: opt?.value || '', buildingName: '' }))}
                  styles={selectStyles} placeholder="All Properties"
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              </div>

              <div style={{ minWidth: 180 }}>
                <Select
                  options={buildingOptions}
                  value={buildingOptions.find((o) => o.value === filters.buildingName) || buildingOptions[0]}
                  onChange={(opt) => setFilters((f) => ({ ...f, buildingName: opt?.value || '' }))}
                  styles={selectStyles} placeholder="All Buildings"
                  isDisabled={!filters.propertyId || hasSingleBuilding}
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              </div>

              <div style={{ minWidth: 160 }}>
                <Select
                  options={allCategoryOptions}
                  value={allCategoryOptions.find((o) => o.value === filters.categoryId) || allCategoryOptions[0]}
                  onChange={(opt) => setFilters((f) => ({ ...f, categoryId: opt?.value || '' }))}
                  styles={selectStyles} placeholder="All Categories"
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              </div>

              <div style={{ minWidth: 130 }}>
                <Select
                  options={MONTH_OPTIONS}
                  value={MONTH_OPTIONS.find((m) => m.value === filters.month) || MONTH_OPTIONS[0]}
                  onChange={(opt) => setFilters((f) => ({ ...f, month: opt?.value || '' }))}
                  styles={selectStyles} placeholder="All Months"
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              </div>

              <div style={{ minWidth: 120 }}>
                <Select
                  options={YEAR_OPTIONS}
                  value={YEAR_OPTIONS.find((y) => y.value === filters.year) || YEAR_OPTIONS[0]}
                  onChange={(opt) => setFilters((f) => ({ ...f, year: opt?.value || '' }))}
                  styles={selectStyles} placeholder="All Years"
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              </div>

              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={() => setFilters({ ...emptyFilters })}
                  style={{
                    background: '#FEF2F2', border: '1px solid #FECDD3', borderRadius: 7,
                    color: '#DC2626', fontSize: '0.75rem', fontWeight: 600,
                    padding: '5px 12px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}
                >
                  <FeatherIcon icon="x" size={12} /> Clear
                </button>
              )}

              {activeFilterCount > 0 && (
                <span style={{
                  background: '#4F46E5', color: 'white', borderRadius: 20,
                  padding: '2px 9px', fontSize: '0.72rem', fontWeight: 700,
                }}>
                  {activeFilterCount} active
                </span>
              )}
            </div>

            <VIRReportsTable
              reports={reports}
              loading={tableLoading}
              properties={properties}
              categoryOptions={categoryOptions}
              userRole={user?.role || ''}
              onSaveReport={handleSaveReport}
              onRefresh={() => { fetchReports(); fetchStatusCounts(); }}  // 👈 refresh both
              filters={filters}
            />
          </>
        )}

        {activeTab === TABS.CATEGORIES && (
          <VIRCategoriesTable userRole={user?.role || ''} onRefresh={fetchCategories} />
        )}

        {activeTab === TABS.VENDORS && (
          <VIRVendorsTable userRole={user?.role || ''} onRefresh={fetchCategories} />
        )}

        <VendorsByCategoryModal
          isOpen={vendorsByCatOpen}
          onClose={() => setVendorsByCatOpen(false)}
        />
      </div>
    </div>
  );
};

export { VendorInspectionReport };